import { apiConfig, http } from "@api/base";
import { getSearchTotal, prepareSumQuery } from "@api/utils";
import { invenioConfig } from "@config";
import _forOwn from "lodash/forOwn";
import { serializer } from "./serializer";

const listUrl = "/patrons/";

const get = async (patronPid) => {
  const response = await http.get(`${listUrl}?q=id:${patronPid}`);
  response.data = serializer.fromJSON(response.data.hits.hits[0]);
  return response;
};

const list = async (queryText, wildcard = true) => {
  const response = await http.get(
    `${listUrl}?q=${queryText}${wildcard ? "*" : ""}`
  );
  response.data.total = getSearchTotal(response.data.hits);
  response.data.hits = response.data.hits.hits.map((hit) =>
    serializer.fromJSON(hit)
  );
  return response;
};

class QueryBuilder {
  constructor() {
    this.patronQuery = [];
  }

  withEmail(email) {
    if (!email) {
      throw TypeError("Email argument missing");
    }
    this.patronQuery.push(`email:${email}`);
    return this;
  }

  withName(name, wildcard = false) {
    if (!name) {
      throw TypeError("Name argument missing");
    }
    this.patronQuery.push(`name:${name}${wildcard ? "*" : ""}`);
    return this;
  }

  withPid(pid) {
    if (!pid) {
      throw TypeError("PID argument is missing");
    }
    this.patronQuery.push(`pid:${pid}`);
    return this;
  }

  // User type filtering methods
  withUserType(userType) {
    if (!userType) {
      throw TypeError("User type argument is missing");
    }
    this.patronQuery.push(`user_type:${userType.toLowerCase()}`);
    return this;
  }

  withPatronType(patronType) {
    if (!patronType) {
      throw TypeError("Patron type argument is missing");
    }
    this.patronQuery.push(`patron_type:${patronType.toLowerCase()}`);
    return this;
  }

  withRole(role) {
    if (!role) {
      throw TypeError("Role argument is missing");
    }
    this.patronQuery.push(`role:${role.toLowerCase()}`);
    return this;
  }

  // Filter by multiple user types
  withUserTypes(userTypes) {
    if (!userTypes || !Array.isArray(userTypes) || userTypes.length === 0) {
      throw TypeError("User types array argument is missing or empty");
    }
    const typeQueries = userTypes.map(
      (type) => `user_type:${type.toLowerCase()}`
    );
    this.patronQuery.push(`(${typeQueries.join(" OR ")})`);
    return this;
  }

  // Filter by multiple patron types
  withPatronTypes(patronTypes) {
    if (
      !patronTypes ||
      !Array.isArray(patronTypes) ||
      patronTypes.length === 0
    ) {
      throw TypeError("Patron types array argument is missing or empty");
    }
    const typeQueries = patronTypes.map(
      (type) => `patron_type:${type.toLowerCase()}`
    );
    this.patronQuery.push(`(${typeQueries.join(" OR ")})`);
    return this;
  }

  // Advanced filtering by user category (combines user_type and role)
  withUserCategory(category) {
    if (!category) {
      throw TypeError("User category argument is missing");
    }

    const categoryLower = category.toLowerCase();
    // Search in both user_type and patron_type fields to catch all matches
    this.patronQuery.push(
      `(user_type:${categoryLower} OR patron_type:${categoryLower})`
    );
    return this;
  }

  // Filter students
  withStudents() {
    return this.withUserType("student");
  }

  // Filter faculty
  withFaculty() {
    return this.withUserCategory("faculty");
  }

  // Filter librarians
  withLibrarians() {
    return this.withUserCategory("librarian");
  }

  // Filter visitors
  withVisitors() {
    return this.withUserType("visitor");
  }

  // Filter researchers
  withResearchers() {
    return this.withUserType("researcher");
  }

  // Filter admins
  withAdmins() {
    return this.withUserCategory("admin");
  }

  withCustomField(term) {
    if (!term) {
      throw TypeError("Custom field term argument is missing");
    }
    _forOwn(invenioConfig.PATRONS.customFields, (value, key) =>
      this.patronQuery.push(`${value.field}:${term}`)
    );
    return this;
  }

  qs() {
    return `${prepareSumQuery(this.patronQuery)}`;
  }
}

const queryBuilder = () => {
  return new QueryBuilder();
};

// Helper functions for common user type searches
const searchByUserType = async (userType, wildcard = true) => {
  const query = queryBuilder().withUserType(userType);
  return list(query.qs(), wildcard);
};

const searchByPatronType = async (patronType, wildcard = true) => {
  const query = queryBuilder().withPatronType(patronType);
  return list(query.qs(), wildcard);
};

const searchByUserCategory = async (category, wildcard = true) => {
  const query = queryBuilder().withUserCategory(category);
  return list(query.qs(), wildcard);
};

// Specific user type searches
const searchStudents = async (wildcard = true) => {
  return searchByUserType("student", wildcard);
};

const searchFaculty = async (wildcard = true) => {
  return searchByUserCategory("faculty", wildcard);
};

const searchLibrarians = async (wildcard = true) => {
  return searchByUserCategory("librarian", wildcard);
};

const searchVisitors = async (wildcard = true) => {
  return searchByUserType("visitor", wildcard);
};

const searchResearchers = async (wildcard = true) => {
  return searchByUserType("researcher", wildcard);
};

export const patronApi = {
  searchBaseURL: `${apiConfig.baseURL}${listUrl}`,
  get: get,
  list: list,
  query: queryBuilder,
  // User type based searches
  searchByUserType: searchByUserType,
  searchByPatronType: searchByPatronType,
  searchByUserCategory: searchByUserCategory,
  // Specific searches
  searchStudents: searchStudents,
  searchFaculty: searchFaculty,
  searchLibrarians: searchLibrarians,
  searchVisitors: searchVisitors,
  searchResearchers: searchResearchers,
};
